import { useEffect, useMemo, useState } from 'react'
import type { IStoreSelectedFacet } from '@generated/graphql'
import { useLocation } from '@reach/router'
import QuickFiltersFacets from 'src/components/sections/ProductGalleryV2/filters/QuickFiltersFacets'

import type { Facet } from './types'
import { variantsFilter } from './filtersConfig'
import { BrandFilter } from './BrandFilter'
import { RangePricing } from './PriceRange'
import { FacetSection } from './FacetSection'
import BlueProductFilter from './BlueProductFilter/BlueProductFilter'
import SelectedFacets from './SelectedFacets'
import {
  formatRange,
  getMaxPrice,
  getMinimumPrice,
  handleActiveFacets,
  mapOrderFacets,
} from './utils/facets'
import './filter.scss'
import { DeliveryAndPickup } from './DeliveryAndPickup/DeliveryAndPickup'
import { DeliveryDate } from './DeliveryDate/DeliveryDate'
import { PickupPointFilter } from './PickupPointFilter'

interface FacetsProps {
  testId: string
  facetsv2?: Facet[]
  indicesExpanded: Set<number>
  onFacetChange: (item: IStoreSelectedFacet, type?: 'RANGE' | 'BOOLEAN') => void
}

type FacetType = {
  key: string
  quantity: number
  selected: boolean
  range: {
    from: number
    to: number
  }
  value: string
  label: string
}

const defaultInitialState = {
  key: 'price',
  quantity: 1,
  selected: true,
  range: {
    from: 0,
    to: 0,
  },
  value: 'range',
  label: '',
}

function Facets({ facetsv2, testId, onFacetChange }: Readonly<FacetsProps>) {
  const location = useLocation()

  const [valueActive, setValueActive] = useState<FacetType>(defaultInitialState)

  const items = useMemo(() => {
    const queryURL = location.search.replace('?', '')
    const urlParams = new URLSearchParams(queryURL)
    const rangeFilter = urlParams.get('2')?.split(':')

    const filterActives = facetsv2?.reduce((acc, item) => {
      return handleActiveFacets(acc, item)
    }, [] as Facet[])

    const facetPriceFilter = filterActives?.find((facet) => facet.key === '2')

    if (facetPriceFilter) {
      facetPriceFilter.values = [
        facetPriceFilter?.values[0],
        {
          key: '2',
          value: '3',
          label: '',
          quantity: 1,
          selected: true,
          range: {
            from: Number(rangeFilter?.[0] ?? 0),
            to: Number(rangeFilter?.[1] ?? 0),
          },
        },
        facetPriceFilter.values.slice(-1)[0],
      ]
    }

    return mapOrderFacets(filterActives ?? [], 'name') ?? []
  }, [facetsv2, location.search])

  const blueProduct = facetsv2?.find(
    (facet) => facet.name.toLocaleLowerCase() === 'blueproduct'
  )

  useEffect(() => {
    const isPriceFilterApplied = location.search.includes('%3A')

    const priceFilterValues = location.search
      ?.split('&')
      ?.filter((e) => e.includes('%3A'))[0]
      ?.replace('?', '')
      ?.replace('2=', '')
      ?.split('%3A')

    if (isPriceFilterApplied) {
      setValueActive({
        ...defaultInitialState,
        range: {
          from: Number(priceFilterValues?.[0]),
          to: Number(priceFilterValues?.[1]),
        },
      })

      return
    }

    setValueActive(defaultInitialState)
  }, [location])

  const Facet = ({ facet }: { facet: Facet }) => {
    if (facet.key === 'pickupPoint') {
      return <PickupPointFilter facet={facet} />
    }

    if (facet.name === 'Preço') {
      const maxPrice = getMaxPrice(facet.values)
      const minPrice = getMinimumPrice(facet.values)

      const isPriceFilterActive =
        valueActive.range.from > 0 || valueActive.range.to > 0

      return (
        <RangePricing
          key={facet.key}
          index={facet.key}
          max={Number(maxPrice)}
          min={Number(minPrice)}
          defaultValue={
            isPriceFilterActive
              ? valueActive.range
              : { from: minPrice, to: maxPrice }
          }
          onEnd={(value: number[]) => {
            setValueActive({
              key: 'price',
              quantity: 1,
              selected: true,
              range: {
                from: value[0],
                to: value[1],
              },
              value: 'range',
              label: '',
            })

            onFacetChange(
              { key: '2', value: formatRange(value[0], value[1]) },
              'RANGE'
            )
          }}
        />
      )
    }

    if (!variantsFilter[facet.name]) {
      return null
    }

    if (facet.name === 'Marca') {
      return (
        <BrandFilter
          key={facet.key}
          facet={facet}
          onFacetChange={onFacetChange}
        />
      )
    }

    if (facet.name === 'Tipo de entrega') {
      return <DeliveryAndPickup facet={facet} onChange={onFacetChange} />
    }

    if (facet.name === 'Opções de entrega') {
      return <DeliveryDate facet={facet} onChange={onFacetChange} />
    }

    return (
      <FacetSection
        facet={facet}
        checkboxType={variantsFilter[facet.name].type}
        useAccordion={variantsFilter[facet.name].useAccordion}
        Icon={variantsFilter[facet.name].Icon}
        onFacetChange={onFacetChange}
        defaultNumberOpts={variantsFilter[facet.name].defaultNumberOpts}
      />
    )
  }

  return (
    <div
      key={location.pathname}
      className="filter"
      data-store-filter
      data-testid={testId}
    >
      <SelectedFacets facetsv2={facetsv2} />
      <div className="mb-4">
        <BlueProductFilter
          blueProduct={blueProduct}
          onFacetChange={onFacetChange}
        />

        <QuickFiltersFacets facetsv2={facetsv2} />
      </div>

      {items.mainFacets?.map((facet, i) => (
        <Facet facet={facet} key={i} />
      ))}

      <div className="mt-12">
        <span className="text-xs text-tertiary">Outros filtros</span>
        {items.otherFacets?.map((facet, i) => (
          <Facet facet={facet} key={i} />
        ))}
      </div>
    </div>
  )
}

export default Facets
